/**
 * The five responsible aspects, mirrored from the server's `apr/aspects.ts`.
 *
 * A dimension says who answers it; a framework may override that for its own use. The value
 * decides which of the five owners on an application receives that dimension's questions.
 *
 * MIRRORED, NOT SHARED, because the two portal apps do not import from one another — the web
 * bundle would drag server code in. The list is small and constrained by a CHECK in migration
 * 0083, so a divergence fails loudly at the API rather than silently on screen.
 */

export const ASPECTS = ['Technical', 'Operational', 'Business', 'Security', 'Strategy'] as const;

export type Aspect = (typeof ASPECTS)[number];

/** Who answers it, in the words used on the Application Master Data panel. */
export const ASPECT_OWNER: Record<Aspect, string> = {
  Technical: 'Application Owner',
  Operational: 'Technical Owner / Custodian',
  Business: 'Business Process Owner',
  Security: 'Security Owner',
  Strategy: 'Executive Owner',
};

/** One line on what the aspect covers, for the selector's title and the help text. */
export const ASPECT_HINT: Record<Aspect, string> = {
  Technical: 'The architect — the application as a system.',
  Operational: 'Run and maintain — platform, patching, availability.',
  Business: 'The business process the application serves.',
  Security: 'Security posture, controls and compliance.',
  Strategy: 'The accountable executive — its future and its funding.',
};

/**
 * The chip colours. Deliberately the SAME five hues the platform already uses for status
 * elsewhere in APR, at a 13% tint, so a dense framework tree reads as one palette rather than
 * five new colours.
 */
export const ASPECT_COLOR: Record<Aspect, { fg: string; bg: string }> = {
  Technical: { fg: '#1565c0', bg: 'rgba(21, 101, 192, 0.13)' },
  Operational: { fg: '#00695c', bg: 'rgba(0, 105, 92, 0.13)' },
  Business: { fg: '#6a1b9a', bg: 'rgba(106, 27, 154, 0.13)' },
  Security: { fg: '#c62828', bg: 'rgba(198, 40, 40, 0.13)' },
  Strategy: { fg: '#e65100', bg: 'rgba(230, 81, 0, 0.13)' },
};

export function isAspect(value: unknown): value is Aspect {
  return typeof value === 'string' && (ASPECTS as readonly string[]).includes(value);
}

/** The override wins; otherwise the dimension's own value. Matches the server exactly. */
export function effectiveAspect(category: unknown, override?: unknown): Aspect {
  if (isAspect(override)) return override;
  return isAspect(category) ? category : 'Technical';
}
